import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Upload,
  message,
} from 'antd';
import {
  createMealPlannerBanner,
  deleteMealPlannerBanner,
  getMealPlannerBanners,
  refreshMealPlannerBanners,
  updateMealPlannerBanner,
  uploadMealPlannerBanner,
} from '@/Axios/api/mealPlanner';
import {
  MealPlannerBannerInput,
  MealPlannerBannerMutation,
  MealPlannerBannerRow,
} from '@/Interfaces/I_mealPlanner';

export default function Banners() {
  const [rows, setRows] = useState<MealPlannerBannerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>();
  const [cacheWarning, setCacheWarning] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string>();
  const [preview, setPreview] = useState<string>();
  const [listReady, setListReady] = useState(false);
  const operationInFlight = useRef(false);
  const uploadInFlight = useRef(false);
  const loadVersion = useRef(0);
  const [form] = Form.useForm<MealPlannerBannerInput>();
  const mutationBlocked = busy || loading || uploading || !listReady;

  // Revoke locally generated previews on replacement/unmount. Stored URLs are untouched.
  useEffect(
    () => () => {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    const version = ++loadVersion.current;
    setLoading(true);
    setListReady(false);
    try {
      const response = await getMealPlannerBanners(signal);
      if (!signal?.aborted && version === loadVersion.current) {
        setRows(response.data.banners);
        setError(undefined);
        setListReady(true);
      }
    } catch {
      if (!signal?.aborted && version === loadVersion.current)
        setError('Unable to load banners. Refresh the list before making changes.');
    } finally {
      if (!signal?.aborted && version === loadVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const result = (data: MealPlannerBannerMutation) => {
    setCacheWarning(!data.cacheRefreshed);
    if (data.cacheRefreshed) message.success('Saved');
  };

  const edit = (row?: MealPlannerBannerRow) => {
    if (mutationBlocked || operationInFlight.current) return;
    setEditingId(row?.banner.bannerId);
    form.resetFields();
    form.setFieldsValue(row ? row.banner : { seq: 0, disabled: false });
    setPreview(row?.imageUrl);
    setOpen(true);
  };

  const save = async (values: MealPlannerBannerInput) => {
    if (mutationBlocked || operationInFlight.current || uploadInFlight.current) return;
    operationInFlight.current = true;
    setBusy(true);
    // Explicit projection keeps bannerId/operator metadata out of the request body.
    const body = {
      imageUrl: values.imageUrl,
      redirectUrl: values.redirectUrl,
      seq: values.seq,
      disabled: values.disabled,
    };
    try {
      const response = editingId
        ? await updateMealPlannerBanner(editingId, body)
        : await createMealPlannerBanner(body);
      result(response.data);
      setOpen(false);
    } catch {
      // A network/audit failure can happen after Mongo commits. Do not blindly repeat a create.
      setOpen(false);
      setCacheWarning(true);
      message.error('Save could not be confirmed. Check the refreshed list before retrying.');
    } finally {
      await load();
      operationInFlight.current = false;
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (mutationBlocked || operationInFlight.current) return;
    operationInFlight.current = true;
    setBusy(true);
    try {
      result((await deleteMealPlannerBanner(id)).data);
    } catch {
      setCacheWarning(true);
      message.error('Delete could not be confirmed. Check the refreshed list.');
    } finally {
      await load();
      operationInFlight.current = false;
      setBusy(false);
    }
  };

  const refreshCache = async () => {
    if (busy || loading || open || operationInFlight.current) return;
    operationInFlight.current = true;
    setBusy(true);
    try {
      await refreshMealPlannerBanners();
      setCacheWarning(false);
      message.success('App banner cache refreshed');
    } catch {
      setCacheWarning(true);
      message.error('Cache refresh failed. Please retry.');
    } finally {
      operationInFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          disabled={mutationBlocked || open}
          onClick={() => edit()}
          data-testid="meal-planner-banner-create"
        >
          Add banner
        </Button>
        <Button
          disabled={busy || loading || open}
          onClick={() => load()}
          data-testid="meal-planner-banner-reload"
        >
          Refresh list
        </Button>
        <Button
          disabled={busy || loading || open}
          onClick={refreshCache}
          data-testid="meal-planner-banner-refresh-cache"
        >
          Refresh app cache
        </Button>
      </Space>
      {error && <Alert type="error" message={error} />}
      {cacheWarning && (
        <Alert
          type="warning"
          message="Database changes may already be saved, but app cache refresh is not confirmed. Check the list, then refresh app cache."
        />
      )}
      <Table<MealPlannerBannerRow>
        rowKey={(row) => row.banner.bannerId}
        dataSource={rows}
        loading={loading}
        pagination={false}
        columns={[
          {
            title: 'Image',
            render: (_, row) => <Image width={160} src={row.imageUrl} preview={false} />,
          },
          {
            title: 'Destination',
            render: (_, row) => (
              <span style={{ overflowWrap: 'anywhere' }}>{row.banner.redirectUrl}</span>
            ),
          },
          { title: 'Sequence', render: (_, row) => row.banner.seq },
          { title: 'Status', render: (_, row) => (row.banner.disabled ? 'Disabled' : 'Enabled') },
          {
            title: 'Actions',
            render: (_, row) => (
              <Space>
                <Button
                  disabled={mutationBlocked || open}
                  onClick={() => edit(row)}
                  data-testid="meal-planner-banner-edit"
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Delete this banner?"
                  onConfirm={() => remove(row.banner.bannerId)}
                  disabled={mutationBlocked || open}
                  okButtonProps={
                    { 'data-testid': 'meal-planner-banner-delete-confirm' } as React.ComponentProps<
                      typeof Button
                    >
                  }
                  cancelButtonProps={
                    { 'data-testid': 'meal-planner-banner-delete-cancel' } as React.ComponentProps<
                      typeof Button
                    >
                  }
                >
                  <Button
                    danger
                    disabled={mutationBlocked || open}
                    data-testid="meal-planner-banner-delete"
                  >
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={editingId ? 'Edit banner' : 'Add banner'}
        open={open}
        closable={!busy && !uploading}
        maskClosable={false}
        keyboard={!busy && !uploading}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} data-testid="meal-planner-banner-form">
          <Form.Item
            name="imageUrl"
            label="Image URL"
            rules={[{ required: true, whitespace: true }, { max: 2048 }]}
          >
            <Input readOnly data-testid="meal-planner-banner-image-path" />
          </Form.Item>
          <Upload
            accept="image/*"
            showUploadList={false}
            disabled={busy || uploading}
            beforeUpload={async (file) => {
              if (operationInFlight.current || uploadInFlight.current) return false;
              if (!file.type.startsWith('image/')) {
                message.error('Choose an image');
                return false;
              }
              uploadInFlight.current = true;
              setUploading(true);
              try {
                const response = await uploadMealPlannerBanner(file);
                const path = response.data[0]?.imageUrl;
                if (!path) throw new Error('Missing uploaded image path');
                form.setFieldsValue({ imageUrl: path });
                setPreview(URL.createObjectURL(file));
              } catch {
                message.error('Image upload failed. Please retry.');
              } finally {
                uploadInFlight.current = false;
                setUploading(false);
              }
              return false;
            }}
          >
            <Button
              loading={uploading}
              disabled={busy || uploading}
              data-testid="meal-planner-banner-upload"
            >
              Upload image
            </Button>
          </Upload>
          {preview && <Image width={240} src={preview} preview={false} />}
          <Form.Item
            name="redirectUrl"
            label="Destination (app deep link or HTTPS)"
            rules={[{ required: true, whitespace: true }, { max: 4096 }]}
          >
            <Input.TextArea
              maxLength={4096}
              disabled={busy}
              data-testid="meal-planner-banner-destination"
            />
          </Form.Item>
          <Form.Item
            name="seq"
            label="Sequence (smaller first)"
            rules={[{ required: true }, { type: 'integer', min: -2147483648, max: 2147483647 }]}
          >
            <InputNumber precision={0} disabled={busy} data-testid="meal-planner-banner-sequence" />
          </Form.Item>
          <Form.Item name="disabled" label="Disabled" valuePropName="checked">
            <Switch disabled={busy} data-testid="meal-planner-banner-disabled" />
          </Form.Item>
          <Space>
            <Button
              htmlType="submit"
              type="primary"
              loading={busy}
              disabled={mutationBlocked}
              data-testid="meal-planner-banner-save"
            >
              Save
            </Button>
            <Button
              disabled={busy || uploading}
              onClick={() => setOpen(false)}
              data-testid="meal-planner-banner-cancel"
            >
              Cancel
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
